import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import Header from './components/Header'
import Home from './pages/Home'
import Post from './pages/Post'
import Auth from './pages/Auth'
import CreatePost from './pages/CreatePost'
import Profile from './pages/Profile'
import SearchResults from './pages/SearchResults'
import ProtectedRoute from './components/ProtectedRoute'
import { AnimatePresence } from 'framer-motion'
import ScrollToTop from './components/ScrollToTop'
import PrivacyPolicy from './pages/legal/PrivacyPolicy'
import Terms from './pages/legal/Terms'
import CookiePolicy from './pages/legal/CookiePolicy'
import Disclaimer from './pages/legal/Disclaimer'
import Footer from './components/Footer'
import About from './pages/About'
import Contact from './pages/Contact'
import Loader3D from './components/Loader3D'
import { useState, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import Rss from './pages/Rss'
import AnimatedHome from './pages/AnimatedHome'

// Lazy load components
const HomePage = lazy(() => import('./pages/Home'))
const PostPage = lazy(() => import('./pages/Post'))
const AuthPage = lazy(() => import('./pages/Auth'))

function App() {
  const [isInitializing, setIsInitializing] = useState(true)
  
  useEffect(() => {
    // Simulate initialization time or wait for critical resources
    const timer = setTimeout(() => {
      setIsInitializing(false)
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [])

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <Loader3D size="xl" />
      </div>
    )
  }

  return (
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ThemeProvider>
          <AuthProvider>
            <div className="min-h-screen page-background flex flex-col">
              <div className="page-overlay" />
              
              <div className="relative flex-1">
                <Header />
                <AnimatePresence mode="wait">
                  <main className="container mx-auto px-4 py-8">
                    <Suspense fallback={<Loader3D size="xl" />}>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/animated-home" element={<AnimatedHome />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/post/:id" element={<PostPage />} />
                        <Route path="/auth" element={<AuthPage />} />
                        <Route path="/search" element={<SearchResults />} />
                        
                        {/* Legal Routes */}
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/cookie-policy" element={<CookiePolicy />} />
                        <Route path="/disclaimer" element={<Disclaimer />} />
                        
                        {/* Protected Routes */}
                        <Route element={<ProtectedRoute />}>
                          <Route path="/create-post" element={<CreatePost />} />
                          <Route path="/profile" element={<Profile />} />
                        </Route>
                        <Route path="/rss" element={<Rss />} />
                        <Route path="*" element={
                          <div>404 - Route not found: {window.location.pathname}</div>
                        } />
                      </Routes>
                    </Suspense>
                  </main>
                </AnimatePresence>
                <ScrollToTop />
                <Footer />
              </div>
            </div>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default App
